export type AssetClass = 'Forex' | 'Metals' | 'Indices' | 'Crypto' | 'Energies';

export type Broker = {
  id: string;
  name: string;
  initial: string;
  color: string;
  regulation: string;
  platform: string;
  commission: string;
  leverage: string;
  leverageNum: number;
  accountType: 'Raw' | 'Standard';
  spreadFactor: number;
  swapFactor: number;
};

export type Instrument = {
  symbol: string;
  name: string;
  assetClass: AssetClass;
  category: string;
  refSpread: number;
  spreadUnit: string;
  spreadCostPerLot: number;
  contractSize: number;
  decimals: number;
  pipValue: number;
  minLot: number;
  maxLot: number;
  lotStep: number;
  tickSize: number;
  swapLong: number;
  swapShort: number;
  refPrice: number;
  tradingHours: string;
};

export const BROKERS: Broker[] = [
  {
    id: 'icmarkets',
    name: 'IC Markets',
    initial: 'IC',
    color: '#7c3aed',
    regulation: 'ASIC · CySEC · SCB',
    platform: 'MT4 / MT5 · cTrader',
    commission: '$7 / lot',
    leverage: '1:500',
    leverageNum: 500,
    accountType: 'Raw',
    spreadFactor: 0.75,
    swapFactor: 1.0,
  },
  {
    id: 'pepperstone',
    name: 'XM',
    initial: 'XM',
    color: '#10b981',
    regulation: 'ASIC · FCA',
    platform: 'MT4 / MT5 · cTrader',
    commission: '$7 / lot',
    leverage: '1:500',
    leverageNum: 500,
    accountType: 'Raw',
    spreadFactor: 0.8,
    swapFactor: 0.95,
  },
  {
    id: 'exness',
    name: 'FxPro',
    initial: 'FP',
    color: '#f59e0b',
    regulation: 'FCA · CySEC · FSA',
    platform: 'MT4 / MT5',
    commission: 'Không',
    leverage: '1:Không giới hạn',
    leverageNum: 500,
    accountType: 'Raw Spread',
    spreadFactor: 0.85,
    swapFactor: 1.1,
  },
  {
    id: 'xm',
    name: 'XM',
    initial: 'XM',
    color: '#ec4899',
    regulation: 'ASIC · CySEC · FSC',
    platform: 'MT4 / MT5',
    commission: 'Không',
    leverage: '1:1000',
    leverageNum: 1000,
    accountType: 'Standard',
    spreadFactor: 1.3,
    swapFactor: 1.35,
  },
  {
    id: 'fbs',
    name: 'FBS',
    initial: 'FB',
    color: '#06b6d4',
    regulation: 'CySEC · IFSC',
    platform: 'MT4 / MT5',
    commission: 'Không',
    leverage: '1:3000',
    leverageNum: 3000,
    accountType: 'Standard',
    spreadFactor: 1.4,
    swapFactor: 1.2,
  },
];

export const ASSET_CLASSES: { value: AssetClass | 'all'; label: string }[] = [
  { value: 'all', label: 'Tất cả' },
  { value: 'Forex', label: 'Forex' },
  { value: 'Metals', label: 'Kim loại' },
  { value: 'Indices', label: 'Chỉ số' },
  { value: 'Crypto', label: 'Tiền mã hoá' },
  { value: 'Energies', label: 'Năng lượng' },
];

export const INSTRUMENTS: Instrument[] = [
  { symbol: 'EURUSD', name: 'Euro / Đô la Mỹ', assetClass: 'Forex', category: 'Cặp chính', refSpread: 0.1, spreadUnit: 'pip', spreadCostPerLot: 1.0, contractSize: 100000, decimals: 5, pipValue: 10, minLot: 0.01, maxLot: 100, lotStep: 0.01, tickSize: 0.00001, swapLong: -0.6, swapShort: 0.2, refPrice: 1.085, tradingHours: '24/5' },
  { symbol: 'GBPUSD', name: 'Bảng Anh / Đô la Mỹ', assetClass: 'Forex', category: 'Cặp chính', refSpread: 0.9, spreadUnit: 'pip', spreadCostPerLot: 9.0, contractSize: 100000, decimals: 5, pipValue: 10, minLot: 0.01, maxLot: 100, lotStep: 0.01, tickSize: 0.00001, swapLong: -0.5, swapShort: 0.1, refPrice: 1.27, tradingHours: '24/5' },
  { symbol: 'USDJPY', name: 'Đô la Mỹ / Yên Nhật', assetClass: 'Forex', category: 'Cặp chính', refSpread: 0.7, spreadUnit: 'pip', spreadCostPerLot: 6.0, contractSize: 100000, decimals: 3, pipValue: 8.5, minLot: 0.01, maxLot: 100, lotStep: 0.01, tickSize: 0.001, swapLong: -0.4, swapShort: 0.05, refPrice: 151.2, tradingHours: '24/5' },
  { symbol: 'AUDUSD', name: 'Đô la Úc / Đô la Mỹ', assetClass: 'Forex', category: 'Cặp chính', refSpread: 0.8, spreadUnit: 'pip', spreadCostPerLot: 8.0, contractSize: 100000, decimals: 5, pipValue: 10, minLot: 0.01, maxLot: 100, lotStep: 0.01, tickSize: 0.00001, swapLong: -0.3, swapShort: 0.1, refPrice: 0.658, tradingHours: '24/5' },
  { symbol: 'USDCAD', name: 'Đô la Mỹ / Đô la Canada', assetClass: 'Forex', category: 'Cặp chính', refSpread: 1.0, spreadUnit: 'pip', spreadCostPerLot: 7.5, contractSize: 100000, decimals: 5, pipValue: 7.5, minLot: 0.01, maxLot: 100, lotStep: 0.01, tickSize: 0.00001, swapLong: -0.35, swapShort: 0.08, refPrice: 1.359, tradingHours: '24/5' },
  { symbol: 'USDCHF', name: 'Đô la Mỹ / Franc Thụy Sĩ', assetClass: 'Forex', category: 'Cặp chính', refSpread: 1.2, spreadUnit: 'pip', spreadCostPerLot: 10.5, contractSize: 100000, decimals: 5, pipValue: 8.8, minLot: 0.01, maxLot: 100, lotStep: 0.01, tickSize: 0.00001, swapLong: -0.3, swapShort: 0.05, refPrice: 0.905, tradingHours: '24/5' },
  { symbol: 'NZDUSD', name: 'Đô la New Zealand / Đô la Mỹ', assetClass: 'Forex', category: 'Cặp chính', refSpread: 1.1, spreadUnit: 'pip', spreadCostPerLot: 11.0, contractSize: 100000, decimals: 5, pipValue: 10, minLot: 0.01, maxLot: 100, lotStep: 0.01, tickSize: 0.00001, swapLong: -0.25, swapShort: 0.05, refPrice: 0.612, tradingHours: '24/5' },
  { symbol: 'EURGBP', name: 'Euro / Bảng Anh', assetClass: 'Forex', category: 'Cặp chéo', refSpread: 0.9, spreadUnit: 'pip', spreadCostPerLot: 11.0, contractSize: 100000, decimals: 5, pipValue: 12, minLot: 0.01, maxLot: 100, lotStep: 0.01, tickSize: 0.00001, swapLong: -0.4, swapShort: 0.1, refPrice: 0.854, tradingHours: '24/5' },
  { symbol: 'GBPJPY', name: 'Bảng Anh / Yên Nhật', assetClass: 'Forex', category: 'Cặp chéo', refSpread: 1.5, spreadUnit: 'pip', spreadCostPerLot: 12.75, contractSize: 100000, decimals: 3, pipValue: 8.5, minLot: 0.01, maxLot: 100, lotStep: 0.01, tickSize: 0.001, swapLong: -0.5, swapShort: 0.15, refPrice: 192.0, tradingHours: '24/5' },
  { symbol: 'EURJPY', name: 'Euro / Yên Nhật', assetClass: 'Forex', category: 'Cặp chéo', refSpread: 1.1, spreadUnit: 'pip', spreadCostPerLot: 9.35, contractSize: 100000, decimals: 3, pipValue: 8.5, minLot: 0.01, maxLot: 100, lotStep: 0.01, tickSize: 0.001, swapLong: -0.45, swapShort: 0.12, refPrice: 164.0, tradingHours: '24/5' },
  { symbol: 'USDTRY', name: 'Đô la Mỹ / Lira Thổ Nhĩ Kỳ', assetClass: 'Forex', category: 'Cặp ngoại lai', refSpread: 8.0, spreadUnit: 'pip', spreadCostPerLot: 3.0, contractSize: 100000, decimals: 5, pipValue: 0.3, minLot: 0.01, maxLot: 100, lotStep: 0.01, tickSize: 0.00001, swapLong: -12, swapShort: 5, refPrice: 32.5, tradingHours: '24/5' },
  { symbol: 'USDZAR', name: 'Đô la Mỹ / Rand Nam Phi', assetClass: 'Forex', category: 'Cặp ngoại lai', refSpread: 25, spreadUnit: 'pip', spreadCostPerLot: 13.0, contractSize: 100000, decimals: 5, pipValue: 0.6, minLot: 0.01, maxLot: 100, lotStep: 0.01, tickSize: 0.00001, swapLong: -20, swapShort: 8, refPrice: 18.9, tradingHours: '24/5' },
  { symbol: 'XAUUSD', name: 'Vàng / Đô la Mỹ', assetClass: 'Metals', category: 'Kim loại quý', refSpread: 0.15, spreadUnit: 'point', spreadCostPerLot: 15.0, contractSize: 100, decimals: 2, pipValue: 1, minLot: 0.01, maxLot: 50, lotStep: 0.01, tickSize: 0.01, swapLong: -8, swapShort: 2, refPrice: 2392.0, tradingHours: '23/5' },
  { symbol: 'XAGUSD', name: 'Bạc / Đô la Mỹ', assetClass: 'Metals', category: 'Kim loại quý', refSpread: 0.02, spreadUnit: 'point', spreadCostPerLot: 10.0, contractSize: 5000, decimals: 3, pipValue: 50, minLot: 0.01, maxLot: 50, lotStep: 0.01, tickSize: 0.001, swapLong: -1, swapShort: 0.3, refPrice: 28.5, tradingHours: '23/5' },
  { symbol: 'US30', name: 'Chỉ số Dow Jones 30', assetClass: 'Indices', category: 'Chỉ số Mỹ', refSpread: 1.2, spreadUnit: 'point', spreadCostPerLot: 12.0, contractSize: 1, decimals: 0, pipValue: 1, minLot: 0.1, maxLot: 100, lotStep: 0.1, tickSize: 1, swapLong: -8, swapShort: 3, refPrice: 39100, tradingHours: '23/5' },
  { symbol: 'US500', name: 'Chỉ số S&P 500', assetClass: 'Indices', category: 'Chỉ số Mỹ', refSpread: 0.4, spreadUnit: 'point', spreadCostPerLot: 4.0, contractSize: 10, decimals: 2, pipValue: 10, minLot: 0.1, maxLot: 100, lotStep: 0.1, tickSize: 0.01, swapLong: -4, swapShort: 1.5, refPrice: 5250, tradingHours: '23/5' },
  { symbol: 'NAS100', name: 'Chỉ số Nasdaq 100', assetClass: 'Indices', category: 'Chỉ số Mỹ', refSpread: 1.5, spreadUnit: 'point', spreadCostPerLot: 15.0, contractSize: 10, decimals: 2, pipValue: 10, minLot: 0.1, maxLot: 100, lotStep: 0.1, tickSize: 0.01, swapLong: -6, swapShort: 2, refPrice: 18500, tradingHours: '23/5' },
  { symbol: 'GER40', name: 'Chỉ số DAX 40', assetClass: 'Indices', category: 'Chỉ số châu Âu', refSpread: 2.0, spreadUnit: 'point', spreadCostPerLot: 20.0, contractSize: 1, decimals: 1, pipValue: 1, minLot: 0.1, maxLot: 100, lotStep: 0.1, tickSize: 0.1, swapLong: -5, swapShort: 2, refPrice: 18500, tradingHours: '23/5' },
  { symbol: 'BTCUSD', name: 'Bitcoin / Đô la Mỹ', assetClass: 'Crypto', category: 'Tiền mã hoá', refSpread: 30, spreadUnit: 'USD', spreadCostPerLot: 30.0, contractSize: 1, decimals: 1, pipValue: 1, minLot: 0.01, maxLot: 50, lotStep: 0.01, tickSize: 0.1, swapLong: -50, swapShort: 20, refPrice: 64000, tradingHours: '24/7' },
  { symbol: 'ETHUSD', name: 'Ethereum / Đô la Mỹ', assetClass: 'Crypto', category: 'Tiền mã hoá', refSpread: 2.5, spreadUnit: 'USD', spreadCostPerLot: 2.5, contractSize: 1, decimals: 1, pipValue: 1, minLot: 0.01, maxLot: 50, lotStep: 0.01, tickSize: 0.1, swapLong: -6, swapShort: 2.5, refPrice: 3100, tradingHours: '24/7' },
  { symbol: 'USOIL', name: 'Dầu thô WTI', assetClass: 'Energies', category: 'Năng lượng', refSpread: 0.03, spreadUnit: 'USD', spreadCostPerLot: 30.0, contractSize: 1000, decimals: 2, pipValue: 10, minLot: 0.1, maxLot: 100, lotStep: 0.1, tickSize: 0.01, swapLong: -3, swapShort: 1, refPrice: 78.5, tradingHours: '23/5' },
  { symbol: 'UKOIL', name: 'Dầu thô Brent', assetClass: 'Energies', category: 'Năng lượng', refSpread: 0.04, spreadUnit: 'USD', spreadCostPerLot: 40.0, contractSize: 1000, decimals: 2, pipValue: 10, minLot: 0.1, maxLot: 100, lotStep: 0.1, tickSize: 0.01, swapLong: -3, swapShort: 1, refPrice: 82.3, tradingHours: '23/5' },
];

export const SPREAD_USE_CASES = [
  {
    icon: 'ri-bar-chart-2-line',
    title: 'So sánh chi phí mở lệnh',
    desc: 'Đối chiếu mức spread của nhiều sàn cho cùng một sản phẩm giao dịch.',
  },
  {
    icon: 'ri-filter-3-line',
    title: 'Lọc theo loại tài sản',
    desc: 'Chỉ xem spread của Forex, Kim loại, Chỉ số, Crypto hoặc Năng lượng.',
  },
  {
    icon: 'ri-money-dollar-circle-line',
    title: 'Ước tính chi phí giao dịch',
    desc: 'Quy đổi spread thành chi phí USD thực tế cho một lệnh cụ thể.',
  },
];

export const SWAP_USE_CASES = [
  {
    icon: 'ri-moon-line',
    title: 'Chi phí giữ lệnh qua đêm',
    desc: 'Tính phí swap phải trả hoặc nhận khi giữ vị thế qua nhiều đêm.',
  },
  {
    icon: 'ri-arrow-left-right-line',
    title: 'So sánh Long và Short',
    desc: 'Xem mức swap áp dụng cho từng hướng giao dịch Long hoặc Short.',
  },
  {
    icon: 'ri-calendar-line',
    title: 'Quy đổi theo số đêm',
    desc: 'Nhập số đêm giữ lệnh để tính tổng chi phí swap tích luỹ.',
  },
];

export const PRODUCT_USE_CASES = [
  {
    icon: 'ri-information-line',
    title: 'Kiểm tra thông số chi tiết',
    desc: 'Xem contract size, giá trị pip, khối lượng tối thiểu và tối đa của sản phẩm.',
  },
  {
    icon: 'ri-stack-line',
    title: 'So sánh ký quỹ giữa các sàn',
    desc: 'Tính số tiền ký quỹ cần thiết theo mức đòn bẩy của từng nhà môi giới.',
  },
  {
    icon: 'ri-time-line',
    title: 'Kiểm tra giờ giao dịch',
    desc: 'Xem khung giờ giao dịch và bước giá tối thiểu của từng sản phẩm.',
  },
];

export const WORKFLOW_STEPS = [
  { num: '01', title: 'Chọn loại tài sản', desc: 'Xác định nhóm sản phẩm cần so sánh: Forex, Kim loại, Chỉ số, Crypto hay Năng lượng.' },
  { num: '02', title: 'So sánh Spread', desc: 'Đối chiếu mức spread của nhiều sàn cho cùng một sản phẩm.' },
  { num: '03', title: 'So sánh Swap', desc: 'Tính phí giữ lệnh qua đêm cho từng hướng giao dịch.' },
  { num: '04', title: 'Kiểm tra thông số sản phẩm', desc: 'Xem contract size, giá trị pip và khối lượng giao dịch.' },
  { num: '05', title: 'Đối chiếu chi phí giao dịch', desc: 'Quy đổi spread, swap và ký quỹ thành chi phí USD thực tế.' },
  { num: '06', title: 'Ra quyết định dựa trên dữ liệu', desc: 'Chọn sàn phù hợp với chi phí và thông số mình cần.' },
];

export const TOOL_CHOOSER = [
  {
    icon: 'ri-bar-chart-2-line',
    tag: 'So sánh Spread',
    title: 'Tôi muốn biết sàn nào spread thấp nhất',
    desc: 'Đối chiếu mức spread của nhiều sàn cho cùng một sản phẩm.',
    cta: 'Bắt đầu',
    anchor: 'spread',
  },
  {
    icon: 'ri-moon-line',
    tag: 'So sánh Swap',
    title: 'Tôi thường giữ lệnh qua đêm',
    desc: 'Tính phí swap khi giữ vị thế qua nhiều đêm.',
    cta: 'Bắt đầu',
    anchor: 'swap',
  },
  {
    icon: 'ri-information-line',
    tag: 'Kiểm tra thông số sản phẩm',
    title: 'Tôi cần thông số chi tiết một sản phẩm',
    desc: 'Xem contract size, giá trị pip và khối lượng giao dịch.',
    cta: 'Bắt đầu',
    anchor: 'product',
  },
];

export const METHODOLOGY = [
  {
    title: 'Chi phí Spread',
    icon: 'ri-bar-chart-2-line',
    formula: 'Chi phí = Spread (pip) × Giá trị pip × Khối lượng (lot)',
    desc: 'Spread là chênh lệch giữa giá mua và giá bán. Chi phí spread cho một lệnh được quy đổi thành USD dựa trên giá trị pip và khối lượng giao dịch.',
    symbols: [
      { sym: 'Spread', meaning: 'Chênh lệch giá mua/bán' },
      { sym: 'Giá trị pip', meaning: 'Giá trị tiền của 1 pip' },
      { sym: 'Khối lượng', meaning: 'Số lot giao dịch' },
    ],
  },
  {
    title: 'Chi phí Swap',
    icon: 'ri-moon-line',
    formula: 'Swap = Mức swap (mỗi lot) × Khối lượng × Số đêm',
    desc: 'Swap là lãi suất qua đêm áp dụng khi giữ vị thế qua đêm. Mức swap có thể dương (nhận) hoặc âm (trả), khác nhau theo hướng Long và Short.',
    symbols: [
      { sym: 'Mức swap', meaning: 'Lãi suất qua đêm mỗi lot' },
      { sym: 'Khối lượng', meaning: 'Số lot giao dịch' },
      { sym: 'Số đêm', meaning: 'Số đêm giữ lệnh' },
    ],
  },
  {
    title: 'Ký quỹ (Margin)',
    icon: 'ri-bank-line',
    formula: 'Ký quỹ = (Contract size × Giá) / Đòn bẩy',
    desc: 'Số tiền ký quỹ cần có để mở một vị thế, được tính từ quy mô hợp đồng, giá hiện tại và mức đòn bẩy của sàn.',
    symbols: [
      { sym: 'Contract size', meaning: 'Quy mô hợp đồng' },
      { sym: 'Giá', meaning: 'Giá thị trường hiện tại' },
      { sym: 'Đòn bẩy', meaning: 'Tỷ lệ đòn bẩy' },
    ],
  },
  {
    title: 'Giá trị Pip',
    icon: 'ri-money-dollar-circle-line',
    formula: 'Giá trị pip = (Contract size × Pip) / Tỷ giá',
    desc: 'Giá trị bằng USD của một pip biến động, phụ thuộc vào quy mô hợp đồng và tỷ giá của cặp tiền tệ.',
    symbols: [
      { sym: 'Contract size', meaning: 'Quy mô hợp đồng' },
      { sym: 'Pip', meaning: 'Bước giá tối thiểu' },
      { sym: 'Tỷ giá', meaning: 'Tỷ giá quy đổi' },
    ],
  },
];

export const NOTES = [
  {
    q: 'Spread là gì?',
    a: 'Spread là chênh lệch giữa giá mua (Ask) và giá bán (Bid) của một sản phẩm. Đây là chi phí cơ bản bạn phải trả khi mở một lệnh, thường được tính bằng pip hoặc point.',
  },
  {
    q: 'Swap là gì?',
    a: 'Swap (phí qua đêm) là khoản lãi suất được tính khi bạn giữ một vị thế qua đêm. Tuỳ hướng giao dịch và chênh lệch lãi suất giữa hai đồng tiền, bạn có thể phải trả hoặc nhận swap.',
  },
  {
    q: 'Tại sao spread khác nhau giữa các sàn?',
    a: 'Mỗi sàn có nguồn thanh khoản, mô hình phí (có hoặc không commission) và loại tài khoản khác nhau. Tài khoản Raw thường có spread thấp nhưng thu commission, trong khi tài khoản Standard có spread cao hơn nhưng không thu commission.',
  },
  {
    q: 'Thông số sản phẩm gồm những gì?',
    a: 'Thông số sản phẩm thường gồm contract size, giá trị pip, khối lượng tối thiểu và tối đa, bước khối lượng, bước giá (tick size) và giờ giao dịch.',
  },
  {
    q: 'Chi phí giao dịch được tính như thế nào?',
    a: 'Tổng chi phí một lệnh gồm spread (chi phí mở lệnh), swap (nếu giữ qua đêm) và commission (nếu có). Hiểu rõ cả ba giúp bạn so sánh chi phí thực tế giữa các sàn.',
  },
];

export const COMPARE_RELATED_TOOLS = [
  { icon: 'ri-crosshair-line', title: 'Máy tính Khối lượng', desc: 'Xác định số Lot dựa trên số dư, rủi ro và Stop Loss.', to: '/may-tinh-khoi-luong', accent: '#059669' },
  { icon: 'ri-bank-line', title: 'Máy tính Ký quỹ', desc: 'Tính số tiền ký quỹ cần thiết cho một vị thế.', to: '/may-tinh-ky-quy', accent: '#7c3aed' },
  { icon: 'ri-line-chart-line', title: 'Tính toán Lợi nhuận', desc: 'Ước tính lời lỗ của một giao dịch ở các mức giá.', to: '/tinh-toan-loi-nhuan', accent: '#10b981' },
  { icon: 'ri-shield-line', title: 'Máy tính Rủi ro', desc: 'Tính số tiền có thể chịu rủi ro cho mỗi giao dịch.', to: '/quan-tri-rui-ro', accent: '#f59e0b' },
  { icon: 'ri-route-line', title: 'Máy tính Fibonacci', desc: 'Xác định vùng giá theo tỷ lệ thoái lui hoặc mở rộng.', to: '/phan-tich-ky-thuat', accent: '#d97706' },
];

export const COMPARE_BROKERS = [
  {
    name: 'IC Markets',
    initial: 'IC',
    platform: 'MT4 / MT5 · cTrader',
    spread: 'từ 0.0 pip',
    commission: '$7 / lot',
    leverage: '1:500',
    regulation: 'ASIC · CySEC · SCB',
    color: '#7c3aed',
  },
  {
    name: 'XM',
    initial: 'XM',
    platform: 'MT4 / MT5 · cTrader',
    spread: 'từ 0.0 pip',
    commission: '$7 / lot',
    leverage: '1:500',
    regulation: 'ASIC · FCA',
    color: '#10b981',
  },
  {
    name: 'FxPro',
    initial: 'FP',
    platform: 'MT4 / MT5',
    spread: 'từ 0.0 pip',
    commission: 'Không',
    leverage: '1:Không giới hạn',
    regulation: 'FCA · CySEC · FSA',
    color: '#f59e0b',
  },
];